import React, { useState, useEffect, useRef } from "react";
import { ConversationViewModal } from "./ConversationViewModal";
import { supabase } from "../../lib/supabase";
import type { MarkedLead } from "../../hooks/useMarkedLeads";

// Verdict -> label + Altara design-token colors (light-mode glass system).
const VERDICT_META: Record<
  MarkedLead["dmread_verdict"],
  { label: string; stripe: string; chip: string; dot: string }
> = {
  QUALIFIABLE: {
    label: "Marked good",
    stripe: "border-ds-green",
    chip: "bg-ds-green/10 text-ds-green border-ds-green/30",
    dot: "bg-ds-green",
  },
  BORDERLINE: {
    label: "Borderline",
    stripe: "border-ds-amber",
    chip: "bg-ds-amber/10 text-ds-amber border-ds-amber/30",
    dot: "bg-ds-amber",
  },
  REAL_DQ: {
    label: "Not a fit",
    stripe: "border-black/20",
    chip: "bg-black/[0.05] text-glass-muted border-black/[0.1]",
    dot: "bg-black/30",
  },
};

export const HAS_MONEY = new Set(["had_budget", "walked_back_then_funded"]);

// last_lead_msg_at as a relative age: <1h "{m}m", <24h "{h}h", else "{d}d".
function ageString(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "now";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function metaLine(lead: MarkedLead): string {
  return [lead.dmread_geo, lead.dmread_life_stage, ageString(lead.last_lead_msg_at)]
    .filter(Boolean)
    .join(" · ");
}

// The 24h IG reply window — the urgency signal the RPC computes live.
function WindowBadge({ lead }: { lead: MarkedLead }) {
  if (!lead.in_window || lead.hours_left == null) return null;
  const h = Math.max(0, Math.round(lead.hours_left));
  const urgent = h < 6;
  return (
    <span
      className={`self-center token-11 font-bold px-2 py-0.5 rounded-md border whitespace-nowrap ${
        urgent
          ? "bg-ds-red/10 text-ds-red border-ds-red/30"
          : "bg-ds-accent/10 text-ds-accent border-ds-accent/30"
      }`}
      title="Hours left in the 24h Instagram reply window"
    >
      {h}h left
    </span>
  );
}

interface MarkedLeadsSplitViewProps {
  rows: MarkedLead[];
  timezone: string;
  organizationId: string;
}

export const MarkedLeadsSplitView: React.FC<MarkedLeadsSplitViewProps> = ({
  rows,
  timezone,
  organizationId,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(rows[0]?.id ?? null);
  const [modalOpen, setModalOpen] = useState(false);

  // keep a valid selection as the filtered set changes
  const selected = rows.find((r) => r.id === selectedId) ?? rows[0] ?? null;

  return (
    <div
      data-testid="marked-leads-table"
      className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-4"
    >
      {/* LEFT — compact ledger */}
      <div className="flex flex-col gap-1.5 max-h-[70vh] overflow-y-auto pr-1">
        {rows.map((lead) => {
          const vm = VERDICT_META[lead.dmread_verdict];
          const isSel = selected?.id === lead.id;
          return (
            <button
              key={lead.id}
              type="button"
              data-testid="marked-lead-row"
              onClick={() => setSelectedId(lead.id)}
              className={`anim-tap text-left flex items-stretch gap-3 rounded-xl px-3 py-2.5 border-l-4 ${vm.stripe} ${
                isSel
                  ? "bg-ds-accent/[0.06] border-y border-r border-ds-accent/25"
                  : "hover:bg-black/[0.04] border-y border-r border-transparent"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {lead.needs_response && (
                    <span
                      className="w-2 h-2 rounded-full bg-ds-accent flex-none"
                      title="Lead is waiting on a reply"
                    />
                  )}
                  <span className="token-14 font-semibold text-glass truncate">
                    {lead.lead_instagram || lead.contact_name || "Unknown"}
                  </span>
                  <span className="token-11 text-glass-muted truncate">{metaLine(lead)}</span>
                </div>
                {lead.dmread_one_liner && (
                  <div className="token-13 text-glass-secondary truncate mt-0.5">
                    {lead.dmread_one_liner}
                  </div>
                )}
              </div>
              <div className="self-center flex flex-col items-end gap-1 flex-none">
                <WindowBadge lead={lead} />
                {lead.dmread_money_signal && HAS_MONEY.has(lead.dmread_money_signal) && (
                  <span className="token-11 font-bold text-ds-amber whitespace-nowrap">
                    $ money
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* RIGHT — detail of the selected lead */}
      <div className="rounded-2xl liquid-glass p-5 min-h-[240px]">
        {selected ? (
          <DetailPane
            lead={selected}
            organizationId={organizationId}
            timezone={timezone}
            onOpenThread={() => setModalOpen(true)}
          />
        ) : (
          <div className="h-full grid place-items-center text-glass-muted token-14">
            Select a lead to see the detail.
          </div>
        )}
      </div>

      <ConversationViewModal
        isOpen={modalOpen && selected !== null}
        onClose={() => setModalOpen(false)}
        conversationId={selected?.id ?? null}
        contactName={selected?.contact_name || selected?.lead_instagram || "Unknown"}
        leadInstagram={selected?.lead_instagram}
        timezone={timezone}
        organizationId={organizationId}
      />
    </div>
  );
};

const DetailPane: React.FC<{
  lead: MarkedLead;
  organizationId: string;
  timezone: string;
  onOpenThread: () => void;
}> = ({ lead, organizationId, timezone, onOpenThread }) => {
  const vm = VERDICT_META[lead.dmread_verdict];
  const hasMoney = !!lead.dmread_money_signal && HAS_MONEY.has(lead.dmread_money_signal);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="token-18 font-bold text-glass truncate">
            {lead.lead_instagram || lead.contact_name || "Unknown"}
          </div>
          <div className="token-13 text-glass-muted mt-0.5">{metaLine(lead)}</div>
        </div>
        <div className="flex items-center gap-2 flex-none">
          <WindowBadge lead={lead} />
          <span
            className={`inline-flex items-center gap-1.5 token-13 font-bold px-2.5 py-1 rounded-full border ${vm.chip}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${vm.dot}`} />
            {vm.label}
          </span>
          {hasMoney && (
            <span className="token-11 font-bold px-2 py-1 rounded-md border bg-ds-amber/10 text-ds-amber border-ds-amber/30">
              Has money
            </span>
          )}
        </div>
      </div>

      {lead.dmread_one_liner && (
        <div className="rounded-xl liquid-glass-light px-4 py-3 flex-none">
          <div className="token-11 font-bold tracking-wide text-glass-muted uppercase">
            What Claude saw
          </div>
          <div className="token-14 text-glass-secondary mt-1">{lead.dmread_one_liner}</div>
        </div>
      )}

      {/* The DM thread, loaded inline the moment a lead is selected — no click needed.
          key={lead.id} remounts it per lead, so state resets without a sync setState. */}
      <InlineThread
        key={lead.id}
        conversationId={lead.id}
        organizationId={organizationId}
        timezone={timezone}
      />

      <div className="flex items-center gap-2 flex-wrap flex-none">
        {lead.is_own ? (
          <button
            type="button"
            data-testid="marked-lead-reply"
            onClick={onOpenThread}
            className="anim-tap token-14 font-bold text-white bg-ds-accent rounded-lg px-4 py-2"
          >
            Reply
          </button>
        ) : (
          <button
            type="button"
            data-testid="marked-lead-reply-disabled"
            disabled
            title="Not your lead"
            className="token-14 font-bold rounded-lg px-4 py-2 opacity-40 cursor-not-allowed bg-black/[0.05] text-glass-muted"
          >
            Reply
          </button>
        )}
        {/* Inferred-axis note (geo + life are Claude inferences, per P0) */}
        {(lead.dmread_geo || lead.dmread_life_stage) && (
          <div className="token-11 text-glass-muted ml-auto">
            <span className="text-ds-accent">~ inferred by Claude:</span>{" "}
            {[lead.dmread_geo, lead.dmread_life_stage].filter(Boolean).join(" · ")}
          </div>
        )}
      </div>
    </div>
  );
};

interface ThreadMsg {
  id: string;
  message_text: string | null;
  sent_at: string | null;
  sender_type: string | null;
  message_type: string | null;
}

// Loads the DM thread for the selected conversation and renders it inline in the
// detail pane, so selecting a lead shows the conversation immediately (no modal,
// no "View conversation" click). Same query shape the ConversationViewModal uses:
// ghl_messages by conversation_id (the uuid PK), opportunity-noise filtered.
const InlineThread: React.FC<{
  conversationId: string;
  organizationId: string;
  timezone: string;
}> = ({ conversationId, organizationId, timezone }) => {
  const [msgs, setMsgs] = useState<ThreadMsg[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!supabase || !conversationId) return;
    let cancelled = false;
    (async () => {
      const { data, error: e } = await supabase!
        .from("ghl_messages")
        .select("id, message_text, sent_at, sender_type, message_type")
        .eq("conversation_id", conversationId)
        .eq("organization_id", organizationId)
        .order("sent_at", { ascending: true });
      if (cancelled) return;
      if (e) {
        setError(e.message);
        return;
      }
      const clean = (data as ThreadMsg[]).filter(
        (m) =>
          m.message_text &&
          !m.message_text.includes("Opportunity created") &&
          !m.message_text.includes("Opportunity updated") &&
          !m.message_text.includes("Opportunity deleted")
      );
      setMsgs(clean);
    })();
    return () => {
      cancelled = true;
    };
  }, [conversationId, organizationId]);

  // pin to the newest message once the thread loads
  useEffect(() => {
    if (msgs && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs]);

  const fmtTime = (iso: string | null) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("en-US", {
        timeZone: timezone,
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  return (
    <div
      ref={scrollRef}
      data-testid="inline-thread"
      className="flex-1 min-h-[180px] max-h-[42vh] overflow-y-auto rounded-xl border border-black/[0.08] bg-black/[0.015] p-3 flex flex-col gap-2"
    >
      {error ? (
        <div className="token-13 text-ds-red">Couldn’t load the conversation: {error}</div>
      ) : msgs === null ? (
        <div className="token-13 text-glass-muted">Loading conversation…</div>
      ) : msgs.length === 0 ? (
        <div className="token-13 text-glass-muted">No messages in this conversation.</div>
      ) : (
        msgs.map((m) => {
          const isLead = (m.sender_type || "").toLowerCase() === "lead";
          return (
            <div
              key={m.id}
              className={`max-w-[82%] rounded-2xl px-3 py-2 ${
                isLead
                  ? "self-start bg-black/[0.05] text-glass"
                  : "self-end bg-ds-accent/10 text-glass border border-ds-accent/20"
              }`}
            >
              <div className="token-13 whitespace-pre-wrap break-words">{m.message_text}</div>
              <div className="token-11 text-glass-muted mt-0.5">
                {isLead ? "Lead" : "Setter"} · {fmtTime(m.sent_at)}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};
